import DSBox from "../../system-design/DSBox";
import DSModal from "../../system-design/DSModal";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import useCollectionReviewModal from "../hooks/useCollectionReviewModal";
import useCollectionShareMutation from "../hooks/useCollectionShareMutation";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSButton from "../../system-design/DSButton";
import {
  ApprovableMarketplaceStatus,
  MarketplaceStatus,
  PopulatedDraftCollection,
} from "../../../api/collection";
import ReviewFormModal from "../../../components/forms/ReviewFormModal";
import AssetBox from "./AssetBox";
import CreatorCard from "./CreatorCard";
import DSCard from "../../system-design/DSCard";
import DSCardContent from "../../system-design/DSCardContent";
import { useTheme, Autocomplete, TextField } from "@mui/material";
import KlydoTable from "../../../components/Widgets/KlydoTable";
import DSTooltip from "../../system-design/DSTooltip";
import {
  getPreviewReadiness,
  useShopifyPreviewUrlQuery,
} from "../hooks/useShopifyPreviewUrlQuery";
import ConfirmationModal from "../../ConfirmationModal";
import ColorChip from "./ColorChip";
import { ClocksListResponse, MachineCollection } from "../../../Types";
import adminApiClient from "../../../api/admin";
import firebaseService from "../../../firebase_service/firebaseService";
import { useState } from "react";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../Device/hooks/useListboxInfiniteScroll";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DateTimePicker from "../../system-design/DSDatePicker";
import useSetCollectionEtaMutation from "../hooks/useSetCollectionEtaMutation";
import useUpdateCollectionPlanMutation from "../hooks/useUpdateCollectionPlanMutation";
import AddIcon from "@mui/icons-material/Add";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import { usePlansQuery } from "../../Studio/hooks/usePlans";
import { Plan, PlanStatus } from "../../../api/plan";
import DSAlert from "../../system-design/DSAlert";
import { CollectionReviewStates } from "../../../api/collection";
import DSListItemText from "../../system-design/DSListItemText";

const MARKETPLACE_STATUS_OPTIONS: {
  value: ApprovableMarketplaceStatus;
  label: string;
  description: string;
}[] = [
  {
    value: MarketplaceStatus.Pending,
    label: "Pending",
    description: "Approved for the marketplace. marketing publishes it later",
  },
  {
    value: MarketplaceStatus.False,
    label: "Not for marketplace",
    description: "Never goes to the marketplace",
  },
];

type CollectionReviewModalProps = {
  collection: PopulatedDraftCollection;
  onClose: () => void;
  isModalOpen: boolean;
  hideReviewActions?: boolean;
};
const CollectionReviewModal = ({
  collection,
  isModalOpen,
  onClose,
  hideReviewActions = false,
}: CollectionReviewModalProps) => {
  const { palette } = useTheme();
  const {
    handleRejectButtonClick,
    handleApproveClick,
    isFormModalOpen,
    handleFormModalClose,
    handleSubmitReview,
    reviewState,
    selectedPlanId,
    setSelectedPlanId,
    marketplaceStatus,
    setMarketplaceStatus,
    isSubmitPending,
    submitError,
  } = useCollectionReviewModal({ onCloseReviewModal: onClose, collection });

  const isApprovalReview = reviewState === CollectionReviewStates.Approved;
  const [marketplaceStatusError, setMarketplaceStatusError] = useState(false);
  // The review message, held while the "no plan" warning is open.
  const [pendingApprovalMessage, setPendingApprovalMessage] = useState<
    string | null
  >(null);

  const handleReviewFormSubmit = (message: string) => {
    if (isApprovalReview && !marketplaceStatus) {
      setMarketplaceStatusError(true);
      return;
    }
    // An approved collection cannot get an upfront plan later, so warn before
    // a marketplace collection is approved without a plan.
    if (
      isApprovalReview &&
      !selectedPlanId &&
      marketplaceStatus === MarketplaceStatus.Pending
    ) {
      setPendingApprovalMessage(message);
      return;
    }
    handleSubmitReview(message);
  };

  const handleConfirmApproveWithoutPlan = () => {
    if (pendingApprovalMessage !== null) {
      handleSubmitReview(pendingApprovalMessage);
    }
    setPendingApprovalMessage(null);
  };

  const { data: plans } = usePlansQuery();
  const availablePlans =
    plans?.filter((p) => p.status !== PlanStatus.Deleted) ?? [];
  const selectedPlan = availablePlans.find((p) => p.id === selectedPlanId);
  const artistPlanId = collection.paymentDetails?.planId;
  const artistPlan = availablePlans.find((p) => p.id === artistPlanId);

  const {
    mutate: shareCollection,
    isPending: shareLoading,
    isSuccess: shareSuccess,
  } = useCollectionShareMutation();

  const { data: cachedPreviewData, isFetching: isLoadingPreview } =
    useShopifyPreviewUrlQuery(collection.id, collection);
  const { isReady: isPreviewReady, reason: previewNotReadyReason } =
    getPreviewReadiness(collection);

  const [showAddToClockSection, setShowAddToClockSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClock, setSelectedClock] = useState<ClocksListResponse | null>(
    null,
  );
  const [showAddToClockConfirmation, setShowAddToClockConfirmation] =
    useState(false);

  const existingEta = collection.productPageMetadata?.eta;
  const [etaDate, setEtaDate] = useState<Date>(() =>
    existingEta
      ? new Date(existingEta.year, existingEta.month - 1, existingEta.day)
      : new Date(),
  );
  const { mutate: setEta, isPending: isEtaPending } =
    useSetCollectionEtaMutation();

  const handleSaveEta = () => {
    setEta({
      collectionId: collection.id,
      eta: {
        year: etaDate.getFullYear(),
        month: etaDate.getMonth() + 1,
        day: etaDate.getDate(),
      },
    });
  };

  const handleRemoveEta = () => {
    setEta({ collectionId: collection.id });
  };

  const { mutate: updateCollectionPlan, isPending: isPlanUpdatePending } =
    useUpdateCollectionPlanMutation();

  const handlePlanChange = (planId: string | undefined) => {
    if (!planId) {
      setSelectedPlanId(undefined);
      return;
    }
    const previousPlanId = selectedPlanId;
    setSelectedPlanId(planId);
    updateCollectionPlan(
      { collectionId: collection.id, planId },
      {
        onError: () => {
          setSelectedPlanId(previousPlanId);
          alert("Failed to update the collection plan. Please try again.");
        },
      },
    );
  };

  const {
    data: availableClocks,
    isLoading: isLoadingClocks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({ query: searchQuery });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const isPublished = collection.marketplace?.status === MarketplaceStatus.True;

  const handleViewPreview = () => {
    const url = isPublished
      ? cachedPreviewData?.productUrl
      : cachedPreviewData?.previewUrl;
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleShareClick = () => {
    shareCollection(collection.id, {
      onSuccess: async (shareUrl: string) => {
        await navigator.clipboard.writeText(shareUrl);
      },
    });
  };

  const handleAddToClockClick = () => {
    setShowAddToClockSection(!showAddToClockSection);
  };

  const handleInitiateAddToClock = () => {
    if (selectedClock) {
      setShowAddToClockConfirmation(true);
    }
  };

  const handleConfirmAddToClock = async () => {
    if (!selectedClock) return;

    try {
      // Fetch the clock to get its current machineCollections
      const clock = await adminApiClient.getClock(selectedClock.id);

      const existingCollections: MachineCollection[] =
        clock?.machineCollections?.value || [];

      // Check if collection is already added
      const alreadyAdded = existingCollections.some(
        (c) => c.id === collection.id,
      );

      if (alreadyAdded) {
        alert("This collection is already added to the selected clock.");
        setShowAddToClockConfirmation(false);
        setSelectedClock(null);
        return;
      }

      // Add new collection
      const updatedCollections: MachineCollection[] = [
        ...existingCollections,
        { id: collection.id, permanent: false },
      ];

      // Update the clock
      await firebaseService.updateConfig(
        selectedClock.id,
        "machineCollections",
        updatedCollections,
      );

      alert(
        `Collection successfully added to clock ${selectedClock.idf || selectedClock.id}`,
      );
      setShowAddToClockConfirmation(false);
      setSelectedClock(null);
    } catch (error) {
      console.error("Error adding collection to clock:", error);
      alert("Failed to add collection to clock. Please try again.");
    }
  };

  const handleCancelAddToClock = () => {
    setShowAddToClockConfirmation(false);
    setSelectedClock(null);
  };

  return (
    <DSModal open={isModalOpen} onClose={onClose}>
      <DSBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          overflowY: "auto",
          height: "90%",
          width: "80%",
          p: 4,
          borderRadius: 2,
        }}
      >
        <DSBox
          sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${palette.divider}` }}
        >
          <DSTypography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {collection.name}
          </DSTypography>
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <DSTypography
              variant="subtitle1"
              sx={{
                color:
                  collection.reviewStatus?.state === "approved"
                    ? "success.main"
                    : collection.reviewStatus?.state === "rejected"
                      ? "error.main"
                      : "warning.main",
                fontWeight: 500,
                textTransform: "capitalize",
              }}
            >
              Status: {collection.reviewStatus?.state || "pending"}
            </DSTypography>
            <DSTypography
              variant="body2"
              sx={{ color: palette.text.secondary }}
            >
              ID: {collection.id}
            </DSTypography>
          </DSBox>
          {/* A label with nothing after it says nothing, so an empty note or
              reply drops its whole line. */}
          {collection.reviewStatus?.message && (
            <DSTypography variant="body2" sx={{ mt: 1 }}>
              Message: {collection.reviewStatus.message}
            </DSTypography>
          )}
          {/* The artist's reply to that message, sent with the resubmission this
              request is. */}
          {collection.reviewStatus?.comment && (
            <DSTypography variant="body2" sx={{ mt: 1 }}>
              Artist Comment: {collection.reviewStatus.comment}
            </DSTypography>
          )}
        </DSBox>

        <DSBox sx={{ display: "flex" }}>
          <DSBox
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DSCard sx={{ p: 3 }}>
              <DSCardContent sx={{ p: 0 }}>
                <DSTypography
                  variant="subtitle2"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: palette.text.secondary,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  Visual Assets
                </DSTypography>
                <DSBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, 1fr)",
                    gap: 2,
                  }}
                >
                  <AssetBox
                    label="*Cover"
                    imageUrl={collection.coverUrl}
                    alt="Collection Cover"
                  />

                  <AssetBox
                    label="*Logotype"
                    imageUrl={collection.productPageMetadata?.logoTypeUrl}
                    alt="Logo Type"
                  />

                  <DSBox>
                    <AssetBox
                      label="*Logo symbol"
                      imageUrl={collection.logoUrl}
                      alt="Logo Symbol"
                    />
                    <DSBox sx={{ mt: 1 }}>
                      <DSTypography
                        variant="caption"
                        sx={{ color: palette.text.secondary }}
                      >
                        Hold Pendulum Logo
                      </DSTypography>
                      <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                        {collection.holdPendulumLogo ? "Yes" : "No"}
                      </DSTypography>
                    </DSBox>
                  </DSBox>

                  <DSBox>
                    <AssetBox
                      label="*Thumbnail background"
                      imageUrl={
                        collection.productPageMetadata?.thumbnailBackgroundUrl
                      }
                      alt="Thumbnail Background"
                    />
                    <DSBox sx={{ mt: 1 }}>
                      <DSTypography
                        variant="caption"
                        sx={{
                          color: palette.text.secondary,
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        *Contrast Color
                      </DSTypography>
                      {collection.productPageMetadata?.badgeColorHex ? (
                        <ColorChip
                          color={collection.productPageMetadata.badgeColorHex}
                        />
                      ) : (
                        <DSChip
                          label="Not Set"
                          size="small"
                          sx={{
                            backgroundColor: palette.warning.light,
                            color: palette.warning.dark,
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </DSBox>
                  </DSBox>
                </DSBox>
              </DSCardContent>
            </DSCard>

            <DSCard sx={{ p: 3 }}>
              <DSCardContent sx={{ p: 0 }}>
                <DSTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Collection Details
                </DSTypography>
                <DSBox
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 2,
                  }}
                >
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{ color: palette.text.secondary }}
                    >
                      Artist&apos;s plan
                    </DSTypography>
                    {artistPlan ? (
                      <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                        {artistPlan.title ?? artistPlan.id} — $
                        {artistPlan.price} ·{" "}
                        {artistPlan.upfrontAmount > 0
                          ? `$${artistPlan.upfrontAmount} upfront`
                          : "No upfront"}{" "}
                        ·{" "}
                        {artistPlan.revSharePercentage > 0
                          ? `${artistPlan.revSharePercentage}% rev share`
                          : "No rev share"}
                      </DSTypography>
                    ) : (
                      <DSTypography variant="body2" color="text.secondary">
                        No plan selected
                      </DSTypography>
                    )}
                    {selectedPlanId && selectedPlanId !== artistPlanId && (
                      <DSTypography
                        variant="caption"
                        sx={{ color: "warning.main" }}
                      >
                        Admin override:{" "}
                        {selectedPlan?.title ??
                          selectedPlan?.id ??
                          selectedPlanId}
                      </DSTypography>
                    )}
                    {collection.paymentDetails?.isAdvancedActive && (
                      <DSTypography
                        variant="caption"
                        sx={{ color: "warning.main" }}
                      >
                        Advance active — recoupment in progress
                      </DSTypography>
                    )}
                  </DSBox>
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{ color: palette.text.secondary }}
                    >
                      Created At
                    </DSTypography>
                    <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                      {collection.createdAt
                        ? new Date(collection.createdAt).toLocaleDateString()
                        : "N/A"}
                    </DSTypography>
                  </DSBox>
                  <DSBox>
                    <DSTypography
                      variant="caption"
                      sx={{ color: palette.text.secondary }}
                    >
                      Updated At
                    </DSTypography>
                    <DSTypography variant="body2" sx={{ fontWeight: 500 }}>
                      {collection.updatedAt
                        ? new Date(collection.updatedAt).toLocaleDateString()
                        : "N/A"}
                    </DSTypography>
                  </DSBox>
                </DSBox>
                {availablePlans.length > 0 && (
                  <DSBox sx={{ mt: 2 }}>
                    <DSTypography
                      variant="caption"
                      sx={{
                        color: palette.text.secondary,
                        display: "block",
                        mb: 1,
                      }}
                    >
                      Monetization Plan
                    </DSTypography>
                    <DSSelect
                      fullWidth
                      value={selectedPlanId ?? ""}
                      onChange={(e) => handlePlanChange(e.target.value)}
                      displayEmpty
                      size="small"
                      disabled={isPlanUpdatePending}
                    >
                      <DSMenuItem value="">
                        <em>No plan</em>
                      </DSMenuItem>
                      {availablePlans.map((plan: Plan) => (
                        <DSMenuItem key={plan.id} value={plan.id}>
                          {plan.title ? `${plan.title} — ` : ""}${plan.price} ·{" "}
                          {plan.upfrontAmount > 0
                            ? `$${plan.upfrontAmount} upfront`
                            : "No upfront"}{" "}
                          ·{" "}
                          {plan.revSharePercentage > 0
                            ? `${plan.revSharePercentage}% rev share`
                            : "No rev share"}
                          {plan.isAdvanced ? " (Advanced)" : ""}
                          {plan.status === PlanStatus.Unlisted
                            ? " [Unlisted]"
                            : ""}
                        </DSMenuItem>
                      ))}
                    </DSSelect>
                  </DSBox>
                )}
                <DSBox sx={{ mt: 2 }}>
                  <DSTypography
                    variant="caption"
                    sx={{
                      color: palette.text.secondary,
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Labels
                  </DSTypography>
                  {collection.productPageMetadata?.labels &&
                  collection.productPageMetadata.labels.length > 0 ? (
                    <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {collection.productPageMetadata.labels.map(
                        (label, index) => (
                          <DSChip
                            key={index}
                            label={label}
                            variant="outlined"
                            size="small"
                          />
                        ),
                      )}
                    </DSBox>
                  ) : (
                    <DSTypography
                      variant="body2"
                      sx={{
                        color: palette.text.disabled,
                        fontStyle: "italic",
                      }}
                    >
                      No labels added
                    </DSTypography>
                  )}
                </DSBox>
                {hideReviewActions && (
                  <DSBox sx={{ mt: 2 }}>
                    <DSTypography
                      variant="caption"
                      sx={{
                        color: palette.text.secondary,
                        display: "block",
                        mb: 1,
                      }}
                    >
                      ETA
                    </DSTypography>
                    <DSBox
                      sx={{ display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <DateTimePicker
                        selectedDate={etaDate}
                        onChange={setEtaDate}
                        minDate={new Date()}
                        isToday={(d) =>
                          d.toDateString() === new Date().toDateString()
                        }
                      />
                      <DSButton
                        variant="contained"
                        size="small"
                        onClick={handleSaveEta}
                        disabled={isEtaPending}
                      >
                        {isEtaPending ? "Saving..." : "Save ETA"}
                      </DSButton>
                      {existingEta && (
                        <DSButton
                          variant="outlined"
                          size="small"
                          color="error"
                          onClick={handleRemoveEta}
                          disabled={isEtaPending}
                        >
                          Remove
                        </DSButton>
                      )}
                    </DSBox>
                  </DSBox>
                )}

                {collection.description && (
                  <DSBox sx={{ mt: 2 }}>
                    <DSTypography
                      variant="caption"
                      sx={{ color: palette.text.secondary }}
                    >
                      Description
                    </DSTypography>
                    <DSTypography
                      variant="body2"
                      sx={{
                        mt: 1,
                        p: 2,
                        backgroundColor: palette.action.hover,
                        borderRadius: 1,
                        lineHeight: 1.6,
                      }}
                    >
                      {collection.description}
                    </DSTypography>
                  </DSBox>
                )}
              </DSCardContent>
            </DSCard>
          </DSBox>

          <DSBox sx={{ width: 350 }}>
            <DSTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Creator Information
            </DSTypography>
            <CreatorCard creator={collection.creator} />
          </DSBox>
        </DSBox>

        <DSBox sx={{ mt: 3 }}>
          <DSTypography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Collection Klydos ({collection.klydos?.length || 0})
          </DSTypography>
          <DSBox
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: 1,
              overflow: "auto",
              maxHeight: 400,
            }}
          >
            <KlydoTable
              klydos={collection.klydos || []}
              canRemove={false}
              showHeaders={true}
            />
          </DSBox>
        </DSBox>

        {/* Add to Clock Section */}
        {showAddToClockSection && (
          <DSBox sx={{ mt: 3 }}>
            <DSCard sx={{ p: 3, backgroundColor: palette.action.hover }}>
              <DSCardContent sx={{ p: 0 }}>
                <DSTypography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: "bold" }}
                >
                  Add Collection to Clock
                </DSTypography>
                <DSBox sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                  <Autocomplete
                    sx={{ flexGrow: 1 }}
                    options={availableClocks || []}
                    filterOptions={noClientFilter}
                    getOptionLabel={(option) =>
                      `${option.idf || option.id} (${option.id})`
                    }
                    value={selectedClock}
                    onChange={(_, newValue) => setSelectedClock(newValue)}
                    inputValue={searchQuery}
                    onInputChange={(_, newInputValue) =>
                      setSearchQuery(newInputValue)
                    }
                    loading={isLoadingClocks || isFetchingNextPage}
                    ListboxProps={listboxProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search for clocks..."
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingClocks && (
                                <DSCircularProgress size={20} />
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <DSBox>
                          <DSTypography
                            variant="body2"
                            sx={{ fontWeight: "bold" }}
                          >
                            {option.idf || option.id}
                          </DSTypography>
                          <DSTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            ID: {option.id}
                            {option.registerProduct && " • Registered"}
                          </DSTypography>
                        </DSBox>
                      </li>
                    )}
                    noOptionsText={
                      searchQuery
                        ? "No clocks found"
                        : "Type to search for clocks"
                    }
                  />
                  <DSButton
                    variant="contained"
                    size="medium"
                    startIcon={<AddIcon />}
                    onClick={handleInitiateAddToClock}
                    disabled={!selectedClock}
                    sx={{
                      backgroundColor: "#007bff",
                      "&:hover": { backgroundColor: "#0056b3" },
                    }}
                  >
                    Add
                  </DSButton>
                </DSBox>
              </DSCardContent>
            </DSCard>
          </DSBox>
        )}

        <DSBox
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${palette.divider}`,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <DSBox sx={{ display: "flex", gap: 2 }}>
            <DSButton
              onClick={handleShareClick}
              style={{
                color: shareSuccess ? "white" : "black",
                backgroundColor: shareSuccess ? "green" : "lightgray",
              }}
            >
              {shareLoading
                ? "Sharing..."
                : shareSuccess
                  ? "Copied!"
                  : "Copy Share Link"}
            </DSButton>
            <DSTooltip
              title={
                !isPublished && !isPreviewReady ? previewNotReadyReason : ""
              }
              placement="top"
            >
              <span style={{ display: "inline-flex" }}>
                <DSButton
                  onClick={handleViewPreview}
                  disabled={
                    isLoadingPreview ||
                    (isPublished
                      ? !cachedPreviewData?.productUrl
                      : !isPreviewReady || !cachedPreviewData?.previewUrl)
                  }
                  style={{
                    color: "black",
                    backgroundColor: "lightgray",
                  }}
                >
                  {isLoadingPreview
                    ? "Loading..."
                    : isPublished
                      ? "Go to Shopify Page"
                      : "View Shopify Preview"}
                </DSButton>
              </span>
            </DSTooltip>
            <DSButton
              onClick={handleAddToClockClick}
              style={{
                color: "white",
                backgroundColor: showAddToClockSection ? "#6c757d" : "#007bff",
              }}
            >
              {showAddToClockSection ? "Hide Add to Clock" : "Add to Clock"}
            </DSButton>
          </DSBox>
          {!hideReviewActions && (
            <DSBox sx={{ display: "flex", gap: 2 }}>
              <DSSecondaryButton onClick={handleRejectButtonClick}>
                Reject Collection
              </DSSecondaryButton>
              <DSButton
                variant="contained"
                onClick={handleApproveClick}
                sx={{
                  backgroundColor: "success.main",
                  color: "success.contrastText",
                  "&:hover": { backgroundColor: "success.dark" },
                }}
              >
                Approve
              </DSButton>
            </DSBox>
          )}
        </DSBox>

        {!hideReviewActions && (
          <ReviewFormModal
            open={isFormModalOpen}
            handleClose={handleFormModalClose}
            handleSubmitReview={handleReviewFormSubmit}
            state={reviewState}
            loading={isSubmitPending}
          >
            <DSBox sx={{ mt: 2 }}>
              <DSTypography
                variant="caption"
                sx={{
                  color: palette.text.secondary,
                  display: "block",
                  mb: 1,
                }}
              >
                Marketplace
              </DSTypography>
              <DSSelect
                fullWidth
                displayEmpty
                size="small"
                error={marketplaceStatusError}
                value={marketplaceStatus ?? ""}
                onChange={(e) => {
                  setMarketplaceStatus(
                    e.target.value as ApprovableMarketplaceStatus,
                  );
                  setMarketplaceStatusError(false);
                }}
                renderValue={(value) =>
                  value ? (
                    MARKETPLACE_STATUS_OPTIONS.find(
                      (option) => option.value === value,
                    )?.label
                  ) : (
                    <em>Choose a marketplace status</em>
                  )
                }
              >
                {MARKETPLACE_STATUS_OPTIONS.map((option) => (
                  <DSMenuItem key={option.value} value={option.value}>
                    <DSListItemText
                      primary={option.label}
                      secondary={option.description}
                    />
                  </DSMenuItem>
                ))}
              </DSSelect>
              {marketplaceStatusError && (
                <DSTypography
                  variant="caption"
                  sx={{ color: "error.main", display: "block", mt: 0.5 }}
                >
                  Choose a marketplace status before submitting
                </DSTypography>
              )}
            </DSBox>
            {submitError && (
              <DSAlert severity="error" sx={{ mt: 1 }}>
                {(submitError as Error)?.message ||
                  `Failed to ${reviewState === CollectionReviewStates.Approved ? "approve" : "reject"} collection`}
              </DSAlert>
            )}
          </ReviewFormModal>
        )}

        <ConfirmationModal
          open={pendingApprovalMessage !== null}
          title="This collection has no plan"
          message="If it is going to the marketplace, the artist cannot get an upfront payment after approval. Approve anyway?"
          confirmLabel="Approve anyway"
          cancelLabel="Cancel"
          onClose={() => setPendingApprovalMessage(null)}
          onConfirm={handleConfirmApproveWithoutPlan}
        />

        <ConfirmationModal
          open={showAddToClockConfirmation}
          onClose={handleCancelAddToClock}
          onConfirm={handleConfirmAddToClock}
          message={`Are you sure you want to add the collection "${collection.name}" to clock ${selectedClock?.idf || selectedClock?.id}?`}
        />
      </DSBox>
    </DSModal>
  );
};

export default CollectionReviewModal;
